import { FC, useEffect, useState } from 'react';
import { ContentBox } from '../pages/contentBox';
import { ActionAreaProps } from '../pages/head/actionArea';
import { DataViewEditBoxHead } from './head';
import { DataViewEditBoxNoHead } from './noHead';

type Head = { noHead?: false } & {
  title?: string;
  subtitle?: string;
  startComponent?: JSX.Element;
};
type NoHead = { noHead: true };

type Props = {
  view: (changeToEditMode: () => void) => JSX.Element;
  edit: (changeToViewMode: () => void) => JSX.Element;
  canEdit: boolean;
  canSubmit: boolean;
  initMode?: Mode;
  onCancel?: () => void;
  onSubmit?: () => void;
  onModeChanged?: (mode: Mode) => void;
  actionAreaProps?: ActionAreaProps;
  sidebarComponent?: JSX.Element;
  sidebarComponentWidth?: string;
  noPadding?: boolean;
} & (Head | NoHead);

export type Mode = 'view' | 'edit';

export type DataViewEditBoxProps = Props;
/**
 * DataViewEditBox switches between a view and edit mode. Edit mode only available
 * when canEdit is true.
 *
 * Use DataGrid or FetchedDataGrid for view mode.
 *
 * Use Form or UpdateForm for edit mode.
 */
export const DataViewEditBox: FC<Props> = ({
  view,
  edit,
  canEdit,
  canSubmit = true,
  initMode = 'view',
  onCancel,
  onSubmit,
  onModeChanged,
  actionAreaProps,
  sidebarComponent,
  sidebarComponentWidth,
  noPadding,
  ...headProps
}) => {
  const [mode, setMode] = useState<Mode>(initMode);

  useEffect(() => {
    setMode(initMode);
    onModeChanged?.(initMode);
  }, [initMode]);

  const renderMode = () => {
    if (mode === 'view') {
      if (typeof view === 'object') {
        const View = view as FC;
        return <View />;
      } else {
        return view(() => changeMode('edit'));
      }
    }

    if (typeof edit === 'object') {
      const Edit = edit as FC;
      return <Edit />;
    } else {
      return edit(() => changeMode('view'));
    }
  };

  const changeMode = (newMode: 'toggle' | Mode = 'toggle') => {
    if (newMode === 'toggle') {
      newMode = mode === 'view' ? 'edit' : 'view';
    }
    setMode(newMode);
    onModeChanged && onModeChanged(newMode);
  };

  return (
    <ContentBox
      paddingTop={noPadding ? '0' : undefined}
      paddingBottom={noPadding ? '0' : undefined}
      paddingLeft={noPadding ? '0' : undefined}
      paddingRight={noPadding ? '0' : undefined}
      headComponent={
        !headProps.noHead ? (
          <DataViewEditBoxHead
            title={headProps.title}
            subtitle={headProps.subtitle}
            startComponent={headProps.startComponent}
            canEdit={canEdit}
            canSubmit={canSubmit}
            mode={mode}
            changeMode={() => changeMode()}
            onCancel={onCancel}
            onSubmit={onSubmit}
            actionAreaProps={actionAreaProps}
          />
        ) : (
          <></>
        )
      }
      sidebarComponent={sidebarComponent}
      sidebarComponentWidth={sidebarComponentWidth}
    >
      {headProps.noHead && (
        <DataViewEditBoxNoHead canEdit={canEdit} changeMode={() => changeMode('toggle')} />
      )}
      {renderMode()}
    </ContentBox>
  );
};
